/******/ (() => { // webpackBootstrap
/* eslint-disable */
class ProductSwatch extends HTMLElement {
  static rtPatchVersion = '25.3';
  constructor() {
    super();

    /* ===== Bind the event handlers in order to maintain the correct context of 'this' ===== */
    this.onSwatchChangeBound = this.onSwatchChange.bind(this);
    this.onColorSwatchMouseEnterBound = this.onColorSwatchMouseEnter.bind(this);
    this.onSwatchMouseLeaveBound = this.onSwatchMouseLeave.bind(this);
  }

  connectedCallback() {
    this.colorLabelContainer = this.querySelector('[data-color-swatch-name-container]');
    this.colorLabel = this.querySelector('[data-color-swatch-name]');
    this.colorLabelState = this.querySelector('[data-color-swatch-state]');
    this.soldOutString = this.getAttribute('data-swatch-sold-out-string');
    this.colorSwatches = this.querySelectorAll('.swatch-element.color');
    this.sectionId = this.getAttribute('data-section-id');
    this.optionIndex = Number(this.getAttribute('data-option-index') || 0);
    this.hasCombinedListingValues = !!this.querySelector('[data-is-combined-listing="true"]');
    this.variantMatrix = this.getVariantMatrix();
    this.setEventListeners();

    // Run after sibling swatches exist and once more after first paint.
    queueMicrotask(() => this.syncAllSwatchAvailability());
    requestAnimationFrame(() => this.syncAllSwatchAvailability());
    setTimeout(() => this.syncAllSwatchAvailability(), 150);
    this.setColorLabelState();
  }

  setEventListeners() {
    /* ===== Attach the event listeners to the DOM elements ===== */
    this.colorSwatches.forEach(swatch => {
      swatch.addEventListener('mouseenter', this.onColorSwatchMouseEnterBound);
      swatch.addEventListener('mouseleave', this.onSwatchMouseLeaveBound);
    });

    this.addEventListener('change', this.onSwatchChangeBound);
  }

  setColorLabelState() {
    /* ===== Set the color label state based on the active swatch ===== */
    const activeSwatch = this.querySelector('.swatch-element.color.active');
    if (!activeSwatch) return;

    const activeSwatchAvailable = activeSwatch.getAttribute('data-swatch-option-available');
    if (!this.soldOutString || !this.colorLabelState) return;

    // Set color label state (sold out or not)
    this.updateColorLabelState(activeSwatchAvailable);

    /* ===== Keep track of the current label ===== */
    this.currentLabel = '';
    if (this.colorLabel) this.currentLabel = this.colorLabel.textContent;
  }

  updateColorLabelState(available) {
    /* ===== Set the color label state (sold out or not) ===== */
    if (this.colorLabelState) {
      this.colorLabelState.innerText = available === 'false' ? `(${this.soldOutString})` : '';
    }
  }

  onSwatchChange(event) {
    const input = event.target;
    let currentVariant = this.getVariantData(input.id);
    let productUrl = input.getAttribute('data-product-url');
    let productFetchUrl = input.getAttribute('data-product-fetch-url');
    const isCombinedListing = input.getAttribute('data-is-combined-listing') === 'true';

    if (!isCombinedListing && !this.hasCombinedListingValues && this.variantMatrix.length) {
      const selectedValues = this.getSelectedOptionValues();
      const canonicalVariant = this.findVariant(selectedValues);
      productUrl = this.getCanonicalProductUrl(productUrl);

      if (canonicalVariant) {
        currentVariant = canonicalVariant;
        if (productUrl) productFetchUrl = `${productUrl}?variant=${canonicalVariant.id}`;
      } else {
        // Let Shopify resolve the exact checked option-value IDs server-side.
        currentVariant = null;
        const ids = this.getSelectedOptionValueIds().filter(Boolean);
        if (productUrl && ids.length) productFetchUrl = `${productUrl}?option_values=${ids.join(',')}`;
      }

      this.syncAllSwatchAvailability();
    }

    this.emitVariantChangeEvent(currentVariant, productFetchUrl, productUrl, isCombinedListing);
  }

  getVariantMatrix() {
    if (this.hasCombinedListingValues) return [];
    const optionsRoot = this.closest('[data-product-options]');
    const data = optionsRoot?.querySelector('script[data-rt-variant-matrix]');
    if (!data) return [];
    try {
      const parsed = JSON.parse(data.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Unable to parse River\'s Threads variant matrix', error);
      return [];
    }
  }

  getCanonicalProductUrl(fallbackUrl = null) {
    return this.closest('product-details')?.getAttribute('data-url') || fallbackUrl;
  }

  getProductSwatches() {
    const productDetails = this.closest('product-details');
    if (!productDetails) return [this];
    return Array.from(productDetails.querySelectorAll(`product-swatch[data-section-id="${this.sectionId}"]`))
      .sort((a, b) => Number(a.dataset.optionIndex || 0) - Number(b.dataset.optionIndex || 0));
  }

  normalizeOptionValue(value) {
    return value == null ? null : String(value).trim();
  }

  getSelectedOptionValues() {
    return this.getProductSwatches().map(swatch =>
      this.normalizeOptionValue(swatch.querySelector('input[data-product-variant]:checked')?.value ?? null)
    );
  }

  getSelectedOptionValueIds() {
    return this.getProductSwatches().map(swatch =>
      swatch.querySelector('input[data-product-variant]:checked')?.dataset.optionValueId ?? null
    );
  }

  getVariantOptionValues(variant) {
    if (!variant) return [];
    if (Array.isArray(variant.options) && variant.options.length) {
      return variant.options.map(value => this.normalizeOptionValue(value));
    }
    return [variant.option1, variant.option2, variant.option3]
      .filter(value => value != null)
      .map(value => this.normalizeOptionValue(value));
  }

  findVariant(optionValues) {
    if (!Array.isArray(optionValues) || optionValues.some(value => value == null)) return null;
    const wanted = optionValues.map(value => this.normalizeOptionValue(value));
    return this.variantMatrix.find(variant => {
      const actual = this.getVariantOptionValues(variant);
      return actual.length === wanted.length && actual.every((value, index) => value === wanted[index]);
    }) || null;
  }

  syncAllSwatchAvailability() {
    if (this.hasCombinedListingValues || !this.variantMatrix.length) return;
    this.getProductSwatches().forEach(swatch => {
      if (typeof swatch.syncAvailabilityFromMatrix === 'function') swatch.syncAvailabilityFromMatrix(this.variantMatrix);
    });
  }

  syncAvailabilityFromMatrix(matrix = this.variantMatrix) {
    if (this.hasCombinedListingValues || !Array.isArray(matrix) || !matrix.length) return;
    const selectedValues = this.getSelectedOptionValues();
    if (!selectedValues.length || selectedValues.some(value => value == null)) return;

    this.querySelectorAll('.swatch-element').forEach(swatch => {
      const input = swatch.querySelector('input[data-product-variant]');
      if (!input) return;
      const candidate = [...selectedValues];
      candidate[this.optionIndex] = this.normalizeOptionValue(input.value);
      const wanted = candidate.map(value => this.normalizeOptionValue(value));
      const variant = matrix.find(item => {
        const actual = this.getVariantOptionValues(item);
        return actual.length === wanted.length && actual.every((value, index) => value === wanted[index]);
      });
      const available = !!variant?.available;
      swatch.dataset.swatchOptionAvailable = String(available);
      swatch.classList.toggle('available', available);
      swatch.classList.toggle('soldout', !available);
      if (swatch.classList.contains('active') && swatch.classList.contains('color')) {
        this.updateColorLabelState(String(available));
      }
    });
  }

  emitVariantChangeEvent(variant, productFetchUrl, productUrl, isCombinedListing = false) {
    /* ===== Emit the variant:change event ===== */
    eventBus.emit('variant:change', {
      sectionId: this.sectionId,
      variant: variant,
      fetchURL: productFetchUrl,
      productURL: productUrl,
      isCombinedListing: isCombinedListing
    });
  }

  onColorSwatchMouseEnter(event) {
    /* ===== Update the color label on mouse enter ===== */
    this.updateColorLabel(event.currentTarget);
    
    // Add sibling hover active class
    const activeSwatch = this.querySelector('.swatch-element.color.active');
    if (activeSwatch && !event.currentTarget.classList.contains('active')) activeSwatch.classList.add('sibling-hover-active');
  }

  onSwatchMouseLeave() {
    /* ===== Reset the color label on mouse leave ===== */
    // Remove sibling hover active class
    const siblingHoverActiveSwatch = this.querySelector('.swatch-element.sibling-hover-active');
    if (siblingHoverActiveSwatch) siblingHoverActiveSwatch.classList.remove('sibling-hover-active');

    // Reset color label
    this.resetColorLabel();
  }

  updateColorLabel = (swatch) => {
    /* ===== Update the color label based on the swatch value ===== */
    if (!swatch) return;
    const label = swatch.getAttribute('data-value');
    const swatchAvailable = swatch.getAttribute('data-swatch-option-available');

    if (!label || !this.colorLabel) return;
    // Set color label text
    this.colorLabel.textContent = label;
    this.animateColorLabel(label);
    // Set color label state (sold out or not)
    this.updateColorLabelState(swatchAvailable);
  }

  resetColorLabel = () => {
    /* ===== Reset the color label to the active swatch value ===== */
    const activeSwatch = this.querySelector('.swatch-element.color.active');
    if (!activeSwatch || !this.colorLabel) return;

    const activeSwatchValue = activeSwatch.getAttribute('data-value');
    const swatchAvailable = activeSwatch.getAttribute('data-swatch-option-available');
    if (!activeSwatchValue) return;

    // Reset color label text
    this.colorLabel.textContent = activeSwatchValue;
    this.animateColorLabel(activeSwatchValue);
    // Reset color label state (sold out or not)
    this.updateColorLabelState(swatchAvailable);
  }

  animateColorLabel(label) {
    if (label === this.currentLabel) return;

    /* ===== Animate the color label if label has changed ===== */
    this.colorLabelContainer.classList.remove('fade-in-label');
    void this.colorLabelContainer.offsetWidth; // Force reflow to reset the animation
    this.colorLabelContainer.classList.add('fade-in-label');

    /* ===== Keep track of the current label ===== */
    this.currentLabel = label;
  }

  removeEventListeners() {
    /* ===== Remove the event listeners from the DOM elements ===== */
    this.colorSwatches.forEach(swatch => {
      swatch.removeEventListener('mouseenter', this.onColorSwatchMouseEnterBound);
      swatch.removeEventListener('mouseleave', this.onSwatchMouseLeaveBound);
    });

    this.removeEventListener('change', this.onSwatchChangeBound);
  }

  getVariantData(inputId) {
    return JSON.parse(this.getVariantDataElement(inputId).textContent);
  }

  getVariantDataElement(inputId) {
    return this.querySelector(`script[type="application/json"][data-resource="${inputId}"]`);
  }

  disconnectedCallback() {
    /* ===== Remove the event listeners when the element is removed from the DOM ===== */
    this.removeEventListeners();
  }
}

if (!window.customElements.get('product-swatch')) {
  window.customElements.define('product-swatch', ProductSwatch);
}

/******/ })()
;